/**
 * Le Cyrano de la démo — celui qui répond à la place du vrai.
 *
 * ⛔ **La règle qui autorise à filmer : TOUT `/api/conversations*` s'arrête
 * ici.** Pas seulement les chemins que ce fichier connaît — un chemin inconnu
 * rend une réponse vide plutôt que de partir sur le réseau. C'est ce qui
 * garantit qu'aucun message ne peut atterrir chez un vrai fan pendant qu'on
 * tourne, y compris si quelqu'un ajoute demain un endpoint qu'on n'a pas prévu
 * ici : le pire cas est un bouton qui ne fait rien, jamais un envoi réel.
 *
 * Tout le RESTE du cockpit continue de parler à la prod : les créatrices, le
 * Vault, le catalogue d'offres, les lots du jeu. C'est ce qui fait que la démo
 * ressemble au vrai cockpit — parce que c'en est un.
 *
 * 🪶 Le seul chemin détourné hors de `/api/conversations` est
 * `/api/creators/{id}/fan-tags` : le vocabulaire de tags doit être celui des
 * conversations qu'on montre, sinon le filtre par tag propose des étiquettes
 * qu'aucune carte ne porte.
 *
 * 🪶 Et un seul chemin de `/api/conversations` va CHERCHER quelque chose sur
 * le réseau pour répondre : `…/chatting-scripts`, qui relaie la bibliothèque
 * commune (`GET /api/chatting-scripts`). La garde tient quand même — cette
 * route ne porte ni conversation ni fan, elle ne fait que lire, et rien de ce
 * qu'elle rend ne part tout seul. Sans ça le bouton Script ouvre un popover
 * vide, et c'est justement ce qu'on veut filmer.
 */
import type {
  ApiConversationListItem,
  ApiConversationListResponse,
  ApiFanTagAssignment,
  ApiMessage,
} from "@/panels/conversations/atoms";

import type { ItemDePile } from "@/panels/conversations/pileDuChatteur";

import { COULISSES_VIDES } from "@/panels/fan/coulisses";
import type { FanTimeline } from "@/panels/fan/timeline";

import { maCourbe, mesVentes, monAnalytique, monShift, retirer } from "./argent";
import { emettre } from "./bus";
import { laSuggestionDeScript, leConseil, leResume } from "./conseil";
import {
  arreterLeFlow,
  lancerLeFlow,
  lesFlowsPour,
  leRun,
  leRunActif,
  reagirALenvoi,
} from "./flows";
import { MOTS_DU_PRIX, traduireVers } from "./langues";
import {
  assignationsDe,
  catalogueDeTags,
  conversation,
  detailDe,
  idDemo,
  leMonde,
  loperatrice,
  maFile,
  message,
  poserLeMessage,
  tagLite,
  toutesLesConversations,
  type ConversationDemo,
  type Geste,
} from "./monde";
import { paiementDuFan } from "./scenario";

/** Sentinelle : « la démo ne s'en occupe pas, va au réseau ». */
export const PASSER = Symbol("demo:passer");

/**
 * Sentinelle : « cette route existe côté conversations, mais la démo ne la
 * connaît pas ». Le client la traduit en 404.
 *
 * 🩸 Elle rendait `{}` avant le 19-08-2026, et un objet vide MENT : il se
 * présente comme un succès, et le premier `.filter` sur un champ absent fait
 * tomber l'écran dans son ErrorBoundary. Un 404, lui, emprunte le chemin que
 * le cockpit sait déjà tenir — il est déployé AVANT Cyrano, donc « cette route
 * n'existe pas encore » est un état qu'il traverse à chaque mise en ligne.
 */
export const INTROUVABLE = Symbol("demo:introuvable");

export interface RequeteDemo {
  methode: string;
  params?: Record<string, string | number | boolean | null | undefined>;
  json?: unknown;
  /** Le corps d'un envoi multipart (`messages/media`). */
  formulaire?: FormData;
}

/**
 * Le routeur. Rend `PASSER` pour tout ce qui n'appartient pas à la démo.
 *
 * Asynchrone et légèrement retardé : une réponse instantanée ne ressemble pas
 * à une requête réseau, et les états de chargement du cockpit (squelettes,
 * boutons occupés) ne s'afficheraient jamais à l'image.
 */
export async function repondreEnDemo(
  chemin: string,
  requete: RequeteDemo,
): Promise<unknown> {
  const sansQuery = chemin.split("?")[0] ?? chemin;
  const bouts = sansQuery.split("/").filter(Boolean); // ["api","conversations",...]
  if (bouts[0] !== "api") return PASSER;

  // 💸 L'ARGENT du compte de démo (`argent.ts`) — ses ventes, ses courbes, et
  // le retrait. ⛔ TOUT `/api/payroll/*` s'arrête ici : sans cette garde, un
  // clic sur « Retirer » pendant un tournage enverrait un VRAI retrait USDT.
  if (bouts[1] === "payroll") {
    await souffle();
    return bouts[2] === "withdraw" && requete.methode === "POST" ? retirer() : vide(requete.methode);
  }
  if (bouts[1] === "sales-attribution" && bouts[2] === "me") {
    await souffle();
    const query = new URLSearchParams(chemin.split("?")[1] ?? "");
    const brut = requete.params?.period;
    const periode = query.get("period") ?? (brut ? String(brut) : "30d");
    if (bouts[3] === "timeseries") return maCourbe(periode);
    if (bouts[3] === "sales") return mesVentes(periode);
    if (bouts[3] === "analytique") return monAnalytique(periode);
    if (bouts[3] === "shift") {
      const de = query.get("from") ?? String(requete.params?.from ?? "");
      const jusqua = query.get("to") ?? String(requete.params?.to ?? "");
      return monShift(de, jusqua);
    }
    return vide(requete.methode);
  }

  if (bouts[1] === "creators" && bouts[3] === "fan-tags") {
    await souffle();
    return { tags: catalogueDeTags(bouts[2] ?? "") };
  }

  // 🎬 LES FLOWS — ce qui, sous l'adresse d'une créatrice, parle d'UN fan.
  // La prod ne connaît pas nos conversations : la liste annotée pour un fan,
  // le départ d'un flow et ses runs sont servis ici (`flows.ts`). Le reste —
  // la liste nue, la définition d'un flow — est de la configuration, et
  // passe.
  if (bouts[1] === "creators" && bouts[3] === "flows") {
    const reponse = lesFlowsDeLaDemo(bouts, requete, chemin);
    if (reponse === PASSER) return PASSER;
    await souffle();
    return reponse;
  }

  if (bouts[1] !== "conversations") return PASSER;

  await souffle();
  const reponse = router(bouts.slice(2), requete);
  return reponse;
}

/** Un aller-retour réseau plausible — assez pour que les états de chargement
 *  existent, assez court pour que la vidéo ne traîne pas. */
function souffle(): Promise<void> {
  return new Promise((r) => setTimeout(r, 90 + Math.floor(Math.random() * 110)));
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Le routeur                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

function router(bouts: string[], requete: RequeteDemo): unknown {
  // GET /api/conversations
  if (bouts.length === 0) return laListe(requete);

  // 🎯 LA PILE DU CHATTEUR — avant le lookup par id, sinon « pile » et
  //    « suivant » seraient pris pour des identifiants de conversation :
  //    l'écran recevrait un 404 sur la pile et `{}` sur Suivant, c'est-à-dire
  //    un kiosque muet en plein tournage.
  if (bouts[0] === "pile") return lesGestesDeLaPile(bouts[1], requete);
  if (bouts[0] === "suivant" && bouts.length === 1) {
    return laSuivanteDeLaPile(requete);
  }
  if (bouts[0] === "recommandations" && bouts.length === 1) return lesRecommandations();

  const id = bouts[0]!;
  const conv = conversation(id);
  const reste = bouts.slice(1);

  // Une conversation inconnue : on ne va PAS la chercher sur le réseau (ce
  // serait le seul chemin par lequel un vrai fan pourrait réapparaître).
  if (!conv) return vide(requete.methode);

  if (reste.length === 0) return detailDe(conv, false);

  switch (reste[0]) {
    case "chat-lite":
      return detailDe(conv, true);

    case "messages": {
      // Historique : la démo tient tout le fil en mémoire, il n'y a jamais de
      // page plus ancienne à servir.
      if (reste.length === 1) return { messages: [], next_cursor: null };
      // 🩸 `POST …/messages/media` — le média pris SUR L'ORDI (trombone ou
      // glisser-déposer). Ce n'est PAS le chemin du Vault : il est multipart,
      // il n'a pas d'`content_item_id`, et il rend l'`ApiMessage` tout nu.
      // Il tombait ici, dans le `return {}` d'à côté : la bulle optimiste
      // était réconciliée avec un objet vide, donc elle disparaissait. Kayla,
      // 20-08-2026 : « quand j'envoie un média, il s'envoie pas dans le chat. »
      if (reste[1] === "media" && requete.methode === "POST") {
        return envoyerUnFichier(conv, requete);
      }
      return {};
    }

    case "media":
      return {
        media: conv.messages.filter((m) => m.media_url !== null),
        next_cursor: null,
      };

    case "notes": {
      if (requete.methode === "PUT" || requete.methode === "PATCH") {
        const corps = (requete.json ?? {}) as { body?: string };
        conv.note = {
          ...conv.note,
          body: corps.body ?? conv.note.body,
          updated_at: new Date().toISOString(),
        };
      }
      return conv.note;
    }

    case "fan-info": {
      const corps = (requete.json ?? {}) as Record<string, string | null>;
      conv.note = {
        ...conv.note,
        fan_info: { ...conv.note.fan_info, ...corps },
        updated_at: new Date().toISOString(),
      };
      return conv.note;
    }

    case "fan-tags": {
      if (requete.methode === "PUT") {
        const corps = (requete.json ?? {}) as { tag_ids?: string[] };
        const catalogue = catalogueDeTags(conv.fiche.creator_id);
        const gardes = catalogue.filter((t) =>
          (corps.tag_ids ?? []).includes(t.id),
        );
        conv.tags = gardes.map((t) => tagLite(t.name));
        conv.fiche.tags = conv.tags;
      }
      const assignations: ApiFanTagAssignment[] = assignationsDe(conv);
      return { assignments: assignations };
    }

    case "tag-suggestions":
      return { suggestions: [] };

    case "time-waster-eligibility":
      return {
        eligible: true,
        has_purchase: conv.fiche.revenue_cents > 0,
        payment_attempts: 3,
        min_payment_attempts: 2,
        reasons: [],
      };

    case "mark-read": {
      conv.fiche.unread_count = 0;
      return undefined;
    }

    case "chat-action":
    case "mark-seen-telegram":
      return undefined;

    case "cyrano-paused": {
      const corps = (requete.json ?? {}) as { is_paused?: boolean };
      const enPause = corps.is_paused === true;
      // 🪦 Cyrano n'écrit plus (07-10-2026) : l'état se pose, rien ne part.
      conv.fiche.is_cyrano_paused = enPause;
      if (enPause) conv.fiche.signal_workflow = null;
      return { is_paused: enPause };
    }

    case "content-mode": {
      const corps = (requete.json ?? {}) as { mode?: string };
      conv.fiche.content_mode = corps.mode ?? "off";
      return { mode: conv.fiche.content_mode };
    }

    case "cyrano": {
      conv.fiche.is_cyrano_paused = reste[1] !== "start";
      return { ok: true };
    }

    case "cyrano-draft":
      return reste[1] === "cancel" ? undefined : { draft_text: null };

    // 🧭 FRANCIS — le conseil à droite, son résumé, et le flow suggéré
    //    au-dessus du composeur (`conseil.ts`).
    case "conseil":
      return reste[1] === "resume" ? leResume(conv) : leConseil(conv);

    case "suggestion-script":
      return laSuggestionDeScript(conv);

    // 🌍 LA TRADUCTION — ce que le fan écrit, et ce que le chatteur envoie.
    case "traduction":
      return laTraduction(conv, reste.slice(1), requete);

    case "workflows": {
      if (reste[1] === "arreter") {
        conv.fiche.workflow_actif = null;
        conv.fiche.signal_workflow = null;
        return { ok: true };
      }
      // .../workflows/{nom}/ouvrir
      const nom = decodeURIComponent(reste[1] ?? "");
      conv.fiche.workflow_actif = { nom, etape: null, ouvert_le: new Date().toISOString() };
      return { ok: true };
    }

    case "send": {
      const corps = (requete.json ?? {}) as {
        text?: string;
        traduction?: ApiMessage["traduction"];
      };
      return envoyer(
        conv,
        corps.text ?? "",
        "text",
        corps.traduction ? { traduction: corps.traduction } : undefined,
      );
    }

    case "send-voice":
    case "send-sticker":
    case "send-gif":
    case "send-media":
    case "send-media-group": {
      // 🎬 La bulle porte la VRAIE vignette du Vault. Le Vault n'est pas
      // détourné par la démo (cf. l'en-tête) : cette URL sert le média que
      // Kayla vient de choisir, avec la même auth cookie que partout ailleurs.
      // Sans elle, l'envoi laissait une bulle photo… sans photo, et l'aperçu
      // optimiste disparaissait au retour du POST.
      const item = itemDuVault(requete.json);
      const msg = envoyer(conv, legendeDe(requete.json), "photo", {
        media_file_id: item,
        media_url: vignetteDuVault(requete.json),
      }, "media");
      return { message: msg, order_id: null, payment_url: null };
    }

    // 🎬 `/menu` — la carte configurée de la créatrice, dans le fil.
    //
    // 🩸 Cette route tombait dans `vide()` : la démo répondait `{}` au POST,
    // donc AUCUNE bulle n'apparaissait, et les deux phrases qui suivent la
    // carte partaient dans le vide. Kayla, 20-08-2026 : « quand je fais /menu,
    // ça n'envoie pas vraiment le menu. »
    case "send-menu":
      return envoyerLeMenu(conv, requete);

    case "send-poll":
      return envoyer(conv, "sondage envoyé");

    // 🔗 Le fan reçoit le LIEN, seul (Kayla, 13-09-2026) : la bulle porte
    // l'URL en texte, pas de légende, pas de média. Le bouton sous la bulle
    // reste — c'est ce que le cockpit rend d'un `payment_url` en metadata.
    case "send-payment-link": {
      const corps = corpsDe(requete) as {
        amountCents?: number;
        amount_cents?: number | string;
        paymentMethod?: string;
        payment_method?: string;
        offer_key?: string | null;
      };
      const cents = Number(corps.amountCents ?? corps.amount_cents ?? 0) || 0;
      const moyen = corps.paymentMethod ?? corps.payment_method ?? "dropp";
      const orderId = idDemo("order");
      const msg = envoyer(
        conv,
        "https://dropp.fans/demo",
        "text",
        {
          payment_button: {
            label: `Payer ${Math.round(cents / 100)} €`,
            url: "https://dropp.fans/demo",
          },
        },
        "transaction",
      );
      conv.fiche.has_active_order = true;
      // 💶 L'offre en l'air, telle que la liste la sert : la ligne de la pile
      // en lit le montant, et la pile démo en fait son rang « offre ».
      conv.fiche.offre_en_attente = {
        montant_cents: cents,
        devise: "EUR",
        depuis: new Date().toISOString(),
      };
      // 💸 Sur Dropp, le fan clique et c'est réglé : personne ne confirme rien,
      // ni en vrai ni ici. Un moyen manuel reste en attente — le cockpit ne
      // règle plus une vente à la main (12-10-2026).
      if (moyen === "dropp" && cents > 0) paiementDuFan(conv, cents);
      return {
        message: msg,
        order_id: orderId,
        payment_url: moyen === "iban" ? null : "https://dropp.fans/demo",
        payment_method: moyen,
        offer_id: null,
        catalog_offer_key: corps.offer_key ?? null,
      };
    }

    case "send-game-pack": {
      const corps = (requete.json ?? {}) as {
        scenarioIds?: string[];
        amountCents?: number;
        message?: string | null;
        paymentMethod?: string;
      };
      const parties = corps.scenarioIds?.length ?? 1;
      const orderId = idDemo("order");
      const msg = envoyer(
        conv,
        corps.message || `${parties} parties, c'est parti 🎰`,
        "text",
        {
          game_pack: { order_id: orderId, etat: "en_attente", spins: parties, game: "trista" },
          payment_button: {
            label: `Payer ${Math.round((corps.amountCents ?? 0) / 100)} €`,
            url: "https://dropp.fans/demo",
          },
        },
        "transaction",
      );
      conv.fiche.has_active_order = true;
      return {
        message: msg,
        order_id: orderId,
        payment_url: "https://dropp.fans/demo",
        payment_method: corps.paymentMethod ?? "dropp",
      };
    }

    case "resend-game-pack":
      envoyer(conv, "je te remets le lien de tes parties 🎰");
      return { order_id: idDemo("order"), livre: true };

    // 🎬 Le bouton Script du composeur. Il appelle
    // `/api/conversations/{id}/chatting-scripts`, donc il tombe ICI — et il
    // tombait dans `vide()`, ce qui donnait un popover VIDE à l'image alors
    // que la bibliothèque existe et qu'elle est la même pour tout le monde.
    //
    // 🔐 On sert les VRAIS scripts, et ça ne perce pas la garde : la requête
    // qui part est un GET sur `/api/chatting-scripts`, une route qui ne porte
    // ni conversation ni fan et qui n'écrit rien. Ce que la garde interdit,
    // c'est qu'un message ATTEIGNE un vrai fan ; lire une bibliothèque
    // commune n'en approche pas. L'insertion, elle, reste locale : le texte
    // atterrit dans le champ de saisie, et l'envoi repasse par `send` — servi
    // ici, comme le reste.
    case "chatting-scripts":
      return lesVraisScripts();

    case "timeline":
      return laFrise(conv);

    case "coulisses": {
      // 🩸 La FORME vient du contrat, pas de la mémoire : le 19-08 un
      // `{ messages: [], tours: [] }` inventé ici a fait tomber tout le
      // panneau fan (`parcours` absent → `.filter` sur `undefined`).
      const ouvert = conv.fiche.workflow_actif;
      return {
        ...COULISSES_VIDES,
        parcours: ouvert
          ? [
              {
                nom: ouvert.nom,
                statut: "ouvert",
                a_demander: ouvert.etape ?? null,
                ouvert_le: ouvert.ouvert_le ?? conv.fiche.created_at,
                declencheur: conv.fiche.signal_workflow?.declencheur,
              },
            ]
          : [],
        depuis: conv.messages[0]?.created_at ?? null,
      };
    }

    default:
      return vide(requete.methode);
  }
}

/**
 * La bibliothèque de scripts de la prod, telle quelle.
 *
 * 🪤 L'import est DYNAMIQUE, et il le faut : `core/api/client` importe ce
 * module (c'est lui qui appelle `repondreEnDemo`). Un import statique en sens
 * inverse fermerait le cycle au chargement ; différé à l'appel, il se résout
 * quand tout est monté.
 *
 * ⛔ Une panne rend une liste VIDE, jamais une erreur : un popover vide est
 * moche, une modale d'erreur pendant un tournage est pire.
 */
async function lesVraisScripts(): Promise<unknown> {
  try {
    const { api } = await import("@/core/api/client");
    return await api.get<unknown[]>("/api/chatting-scripts");
  } catch {
    return [];
  }
}

/**
 * Un chemin que la démo ne connaît pas. JAMAIS un passage au réseau — cf.
 * l'en-tête du fichier.
 *
 * Une LECTURE rend 404 : le lecteur prendra son chemin « la route n'est pas
 * là », qu'il sait déjà tenir. Une ÉCRITURE rend un objet vide : elle
 * n'attend en général rien de sa réponse, et un 404 y ferait rougir un bouton
 * qui, en démo, n'avait rien à faire de toute façon.
 */
function vide(methode: string): unknown {
  return methode === "GET" ? INTROUVABLE : {};
}

function legendeDe(json: unknown): string {
  const corps = (json ?? {}) as { caption?: string | null; message?: string | null };
  return corps.caption ?? corps.message ?? "";
}

/**
 * Les champs d'un envoi, qu'il soit venu en JSON ou en multipart.
 *
 * En multipart, les champs passent par un `FormData`, tous en `snake_case` et
 * tous en chaîne. Sans cette fusion, la démo lisait un corps vide et affichait
 * « Payer 0 € » (payé quand la transaction avait encore un teaser uploadé).
 */
function corpsDe(requete: RequeteDemo): Record<string, unknown> {
  const duFormulaire: Record<string, unknown> = {};
  requete.formulaire?.forEach((valeur, cle) => {
    if (typeof valeur === "string") duFormulaire[cle] = valeur;
  });
  return { ...duFormulaire, ...((requete.json ?? {}) as Record<string, unknown>) };
}

/**
 * La vignette du premier média choisi, ou `null`.
 *
 * ⛔ Le Vault n'est PAS détourné par la démo : cette URL sert les vrais octets,
 * avec la même auth cookie que partout. C'est ce qui fait qu'un envoi de média
 * ressemble au poste — parce que c'est le média du poste.
 *
 * 🪤 `/thumb` et pas `/preview` : la vignette est un webp pour TOUS les types,
 * y compris les vidéos. `/preview` sur une vidéo rendrait une image cassée, et
 * la démo n'a pas le type sous la main (le serveur le déduit du Vault, et c'est
 * lui qu'on remplace ici).
 */
function itemDuVault(json: unknown): string | null {
  const corps = (json ?? {}) as {
    content_item_id?: string | null;
    content_item_ids?: string[] | null;
    preview_content_item_ids?: string[] | null;
  };
  return (
    corps.content_item_id ??
    corps.content_item_ids?.[0] ??
    corps.preview_content_item_ids?.[0] ??
    null
  );
}

function vignetteDuVault(json: unknown): string | null {
  const id = itemDuVault(json);
  return id ? `/api/vault/items/${encodeURIComponent(id)}/thumb` : null;
}

/** Une offre de la carte qui vient de partir, telle que le contrat la rend. */
interface OffreDeLaCarte {
  offer_key: string;
}

/** Ce qu'on sait faire d'un fichier joint : un identifiant, une URL, un type. */
interface FichierJoint {
  id: string | null;
  url: string | null;
  type: string;
}

const SANS_FICHIER: FichierJoint = { id: null, url: null, type: "photo" };

/**
 * Le fichier d'un envoi multipart, rendu affichable SANS réseau.
 *
 * `URL.createObjectURL` sur les octets que l'opératrice vient de choisir : le
 * fil affiche exactement ce qu'elle a joint, et ça ne sort pas du navigateur —
 * ce qui est précisément la promesse de la démo.
 *
 * 🪤 On ne réutilise PAS le blob de la bulle optimiste : `useUploadMedia` le
 * révoque à `onSettled`, donc l'image mourrait à la seconde où la nôtre prend
 * sa place. Le nôtre vit jusqu'au rechargement de la page — et la démo se
 * recharge à chaque prise, c'est son bouton « recommencer ».
 *
 * ⛔ Le type suit le MIME, pas un défaut : un `<img>` sur une vidéo ne rend
 * rien, et « photo » sur un fichier `video/mp4` est exactement ça.
 */
function fichierDe(requete: RequeteDemo): FichierJoint {
  const brut = requete.formulaire?.get("file");
  if (!(brut instanceof Blob)) return SANS_FICHIER;
  const mime = (brut.type || "").toLowerCase();
  const type = mime.startsWith("video/")
    ? "video"
    : mime.startsWith("audio/")
      ? "voice"
      : "photo";
  let url: string | null = null;
  try {
    url = URL.createObjectURL(brut);
  } catch {
    // Environnement sans `createObjectURL` (banc) : la bulle part sans image
    // plutôt que de faire tomber l'envoi.
    url = null;
  }
  return { id: idDemo("fichier"), url, type };
}

/**
 * Un fichier envoyé depuis l'ordi. Rend l'`ApiMessage` NU — c'est le contrat de
 * `POST …/messages/media`, qui ne l'enveloppe dans rien (contrairement à
 * `send-media`, qui rend `{ message, order_id, payment_url }`). Se tromper
 * d'enveloppe ici, c'est la bulle qui disparaît au lieu d'apparaître.
 */
function envoyerUnFichier(conv: ConversationDemo, requete: RequeteDemo): ApiMessage {
  const fichier = fichierDe(requete);
  const legende = requete.formulaire?.get("caption");
  return envoyer(
    conv,
    typeof legende === "string" ? legende : "",
    fichier.type,
    { media_file_id: fichier.id, media_url: fichier.url },
    "media",
  );
}

/**
 * L'envoi du menu configuré — la carte PNG, puis ce qu'elle dit derrière.
 *
 * La carte part NUE (`content: null`) : c'est l'arbitrage de Kayla du 13-08,
 * tenu par `menu_delivery.py` côté Cyrano et par `lesBullesDuMenu.ts` côté
 * cockpit. Les deux phrases qui la suivent ne sont pas fabriquées ici — elles
 * repassent par `send`, comme au poste.
 */
async function envoyerLeMenu(
  conv: ConversationDemo,
  requete: RequeteDemo,
): Promise<unknown> {
  const corps = corpsDe(requete) as { menu_variant_id?: string };
  const variantId = String(corps.menu_variant_id ?? "");
  const creatorId = conv.fiche.creator_id;
  envoyer(
    conv,
    "",
    "photo",
    {
      content: null,
      // ⛔ Les deux, jamais l'un sans l'autre : c'est `media_file_id` qui
      // décide que la bulle porte une image, et `media_url` qui dit laquelle.
      media_file_id: variantId || null,
      media_url: variantId
        ? `/api/creators/${encodeURIComponent(creatorId)}/menu-variants/${encodeURIComponent(variantId)}/image`
        : null,
    },
    "menu",
  );
  return {
    menu_send_id: idDemo("menu-send"),
    menu_variant_id: variantId,
    menu_items: await lesOffresDeLaCarte(creatorId, variantId),
  };
}

/**
 * Ce que la carte porte VRAIMENT — lu sur le manifeste rendu de la variante.
 *
 * ⛔ On ne conseille jamais ce que le fan ne peut pas voir : la bulle de reco
 * ne nomme une offre que si elle est sur l'image (`lesBullesDuMenu.ts`). Une
 * liste inventée ici ferait dire à la créatrice « je te conseille mon pack
 * découverte » devant une carte qui ne le porte pas.
 *
 * 🔐 La garde tient : `/api/creators/{id}/menu-variants` est un GET qui ne
 * porte ni conversation ni fan et n'écrit rien — même raisonnement que la
 * bibliothèque de scripts, et c'est de toute façon l'appel que `/menu` vient
 * déjà de faire pour choisir la variante.
 *
 * Une panne rend une liste VIDE : le fan reçoit la carte et l'invitation, sans
 * la reco. Dégradé, jamais faux.
 */
async function lesOffresDeLaCarte(
  creatorId: string,
  variantId: string,
): Promise<OffreDeLaCarte[]> {
  if (!creatorId || !variantId) return [];
  try {
    const { api } = await import("@/core/api/client");
    const variantes = await api.get<
      Array<{
        id: string;
        rendered_manifest?: {
          sections?: Array<{ items?: Array<{ catalog_offer_key?: string }> }>;
        } | null;
      }>
    >(`/api/creators/${encodeURIComponent(creatorId)}/menu-variants`);
    const carte = variantes.find((v) => v.id === variantId);
    const sections = carte?.rendered_manifest?.sections ?? [];
    return sections
      .flatMap((s) => s.items ?? [])
      .map((i) => i.catalog_offer_key)
      .filter((cle): cle is string => Boolean(cle))
      .map((offer_key) => ({ offer_key }));
  } catch {
    return [];
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* L'envoi                                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Un envoi de l'opératrice. Le message porte SON identité (comme le backend
 * l'écrit dans `extra_metadata`) — sinon la bulle bascule sur l'avatar de
 * Cyrano au retour du POST, et ça se voit à l'image.
 */
function envoyer(
  conv: ConversationDemo,
  texte: string,
  type = "text",
  extra?: Partial<ApiMessage>,
  geste: Geste = "texte",
): ApiMessage {
  const qui = loperatrice();
  const msg = poserLeMessage(
    conv,
    message({
      direction: "out",
      created_at: new Date().toISOString(),
      content: texte,
      message_type: type,
      is_ai_generated: false,
      sender_source: "operator",
      operator_workos_user_id: qui.workosUserId,
      operator_email: qui.email,
      ...extra,
    }),
  );
  // Le chatteur a repris : le « flow terminé » de la carte s'éteint.
  conv.fiche.moment_a_reprendre = null;
  reagirALenvoi(conv, geste);
  return msg;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* La liste                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */

const GROUPES: Record<string, (stage: string) => boolean> = {
  customers: (s) => s.startsWith("customer"),
  fans: (s) => s === "new" || s.startsWith("fan"),
  gfe: (s) => s === "gfe",
};

/* ────────────────────────────────────────────────────────────────────────── */
/* La pile du chatteur — dérivée du fil, jamais inventée                       */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * 🎯 MA FILE, et ce qui attend dans « Discussions ».
 *
 * Kayla, 07-10-2026 : « je lance le shift, il y a 32 personnes dans ma file.
 * Normalement, quand on lance le shift, il n'y a personne ». La pile servait
 * TOUTES les conversations dont le fan avait écrit en dernier — c'est-à-dire
 * le répertoire, pas la file. Au poste, la file est ce que le chatteur
 * s'AJOUTE (`pile/epingler`, le bouton « Ajouter » de « Discussions ») : elle
 * naît vide, et c'est ce que montre la démo.
 *
 * Le signal de chaque ligne se DÉRIVE de deux ou trois faits de la fiche —
 * une offre en l'air, il vient d'arriver, il demande le prix, il attend —
 * jamais d'une règle recopiée du serveur (`signaux_de_file.py`).
 */
interface Signal {
  signal: string;
  le: string;
  rang: number;
  offre: { montant_cents: number; devise: string; depuis: string } | null;
}

/** Les rangs de `signaux_de_file.ORDRE`, pour ceux que la démo produit. */
const RANG = {
  demande_de_prix: 2,
  nouveau: 3,
  offre_en_attente: 7,
  sans_reponse: 8,
  en_attente_du_fan: 10,
} as const;

/** Au-delà, ce n'est pas du travail à faire : c'est un état. */
const RANG_MAX_A_TRAITER = RANG.sans_reponse;

/** `PILE_PLAFOND` de Cyrano (15 depuis le 14-09-2026). */
const PLAFOND_DE_LA_FILE = 15;

function signalDe(c: ConversationDemo): Signal {
  const offre = c.fiche.has_active_order ? (c.fiche.offre_en_attente ?? null) : null;
  if (offre) {
    return { signal: "offre_en_attente", le: offre.depuis, rang: RANG.offre_en_attente, offre };
  }
  const dernier = c.messages[c.messages.length - 1];
  if (!dernier || dernier.direction !== "in") {
    return {
      signal: "en_attente_du_fan",
      le: dernier?.created_at ?? c.fiche.created_at,
      rang: RANG.en_attente_du_fan,
      offre: null,
    };
  }
  const aDejaRecu = c.messages.some((m) => m.direction === "out");
  if (!aDejaRecu) {
    return { signal: "nouveau", le: c.fiche.created_at, rang: RANG.nouveau, offre: null };
  }
  const dit = (dernier.traductions?.fr ?? dernier.content ?? "").toLowerCase();
  if (MOTS_DU_PRIX.test(dit)) {
    return {
      signal: "demande_de_prix",
      le: dernier.created_at,
      rang: RANG.demande_de_prix,
      offre: null,
    };
  }
  return { signal: "sans_reponse", le: dernier.created_at, rang: RANG.sans_reponse, offre: null };
}

function raisonDe(s: Signal): string {
  const depuis = Math.max(0, Math.round((Date.now() - Date.parse(s.le)) / 60000));
  if (s.offre) return `offre ${Math.round(s.offre.montant_cents / 100)} € · ${depuis} min sans réponse`;
  if (s.signal === "nouveau") return `nouveau · arrivé il y a ${depuis} min`;
  if (s.signal === "demande_de_prix") return `il demande le prix · ${depuis} min`;
  return `${depuis} min sans réponse`;
}

function ligne(conv: ConversationDemo, s: Signal, dansMaFile: boolean): ItemDePile {
  return {
    conversation_id: conv.fiche.id,
    creator_id: conv.fiche.creator_id,
    fan: { username: conv.fiche.fan_name, first_name: conv.fiche.fan_name },
    signal: s.signal,
    signal_le: s.le,
    priorite_rang: s.rang,
    priorite_raison: raisonDe(s),
    attribuee_le: dansMaFile ? new Date().toISOString() : "",
    a_repondre_avant: null,
    epinglee: dansMaFile,
  };
}

function laPile() {
  const file = maFile();
  const pile = [...file]
    .map((id) => conversation(id))
    .filter((c): c is ConversationDemo => c !== undefined)
    .map((c) => ligne(c, signalDe(c), true));
  return {
    pile,
    presents: 1,
    battement_secondes: 30,
    epingles: pile.length,
    epingles_max: PLAFOND_DE_LA_FILE,
    plafond: PLAFOND_DE_LA_FILE,
  };
}

/** Ce qui attend quelqu'un et n'est à personne — « Discussions », trié. */
function aTraiterHorsFile(): Array<{ conv: ConversationDemo; s: Signal }> {
  const file = maFile();
  return toutesLesConversations()
    .filter((c) => !file.has(c.fiche.id))
    .map((conv) => ({ conv, s: signalDe(conv) }))
    .filter(({ s }) => s.rang <= RANG_MAX_A_TRAITER)
    .sort((a, b) => a.s.rang - b.s.rang || Date.parse(a.s.le) - Date.parse(b.s.le));
}

function lesRecommandations() {
  const libres = aTraiterHorsFile();
  return {
    recommandations: libres.map(({ conv, s }) => {
      const l = ligne(conv, s, false);
      return {
        conversation_id: l.conversation_id,
        creator_id: l.creator_id,
        fan: l.fan,
        signal: l.signal,
        signal_le: l.signal_le,
        priorite_rang: l.priorite_rang,
        priorite_raison: l.priorite_raison,
      };
    }),
    total: libres.length,
    prix_dans_l_air: libres.filter(
      ({ s }) => s.signal === "demande_de_prix" || s.signal === "offre_en_attente",
    ).length,
  };
}

function lesGestesDeLaPile(geste: string | undefined, requete: RequeteDemo): unknown {
  const file = maFile();
  const id = ((requete.json ?? {}) as { conversation_id?: string }).conversation_id ?? "";
  const epingle = () => ({
    epinglee: file.has(id),
    epingles: file.size,
    epingles_max: PLAFOND_DE_LA_FILE,
  });
  switch (geste) {
    case undefined:
      return requete.methode === "GET" ? laPile() : vide(requete.methode);
    case "epingler":
      if (conversation(id) && file.size < PLAFOND_DE_LA_FILE) file.add(id);
      return epingle();
    case "desepingler":
      file.delete(id);
      return epingle();
    case "liberer":
      return { libere: file.delete(id), pile: file.size };
    case "remplacer": {
      file.delete(id);
      const suivante = aTraiterHorsFile().find(({ conv }) => conv.fiche.id !== id);
      if (!suivante) return { conversation: null, pile: file.size };
      file.add(suivante.conv.fiche.id);
      return { conversation: ligne(suivante.conv, suivante.s, true), pile: file.size };
    }
    case "quitter": {
      const liberees = file.size;
      file.clear();
      return { liberees };
    }
    default:
      return vide(requete.methode);
  }
}

/** « Suivant » : la tête de MA file, en sautant celle qu'on quitte. */
function laSuivanteDeLaPile(requete: RequeteDemo): {
  conversation: ItemDePile | null;
  pile: number;
  battement_secondes: number;
} {
  const { pile } = laPile();
  const corps = (requete.json ?? {}) as { quitte?: string | null };
  const quitte = corps.quitte ?? null;
  const triee = [...pile].sort(
    (a, b) =>
      a.priorite_rang - b.priorite_rang || Date.parse(a.signal_le) - Date.parse(b.signal_le),
  );
  const suivante = triee.find((i) => i.conversation_id !== quitte) ?? null;
  return { conversation: suivante, pile: pile.length, battement_secondes: 30 };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Les flows d'un fan                                                         */
/* ────────────────────────────────────────────────────────────────────────── */

/** `bouts` = ["api","creators",cid,"flows",…]. `PASSER` = de la configuration. */
function lesFlowsDeLaDemo(bouts: string[], requete: RequeteDemo, chemin: string): unknown {
  const cid = decodeURIComponent(bouts[2] ?? "");
  const query = new URLSearchParams(chemin.split("?")[1] ?? "");
  const brut = requete.params?.conversation_id;
  const convId =
    query.get("conversation_id") ?? (brut !== null && brut !== undefined ? String(brut) : null);

  // La liste du menu Flow ▾ POUR un fan.
  if (bouts.length === 4) {
    if (requete.methode === "GET" && convId) return lesFlowsPour(cid, convId);
    return PASSER;
  }
  if (bouts[4] === "runs") {
    if (bouts[5] === "active") return convId ? leRunActif(convId) : null;
    if (bouts[5] === "last") return convId ? leRun(convId) : null;
    if (bouts[6] === "stop") return arreterLeFlow(bouts[5] ?? "");
    // envoyer-maintenant, reprendre… : rien à jouer de plus ici.
    return requete.methode === "GET" ? null : {};
  }
  if (bouts.length === 6 && bouts[5] === "start" && requete.methode === "POST") {
    const corps = (requete.json ?? {}) as { conversation_id?: string };
    return lancerLeFlow(cid, decodeURIComponent(bouts[4] ?? ""), corps.conversation_id ?? "");
  }
  return PASSER;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* La traduction                                                              */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Sentinelle : « la démo ne sait pas traduire cette phrase ». Le client la
 * rend en erreur lisible (cf. `core/api/client`).
 */
export const TRADUCTION_INCONNUE = Symbol("demo:traduction-inconnue");

/**
 * `…/traduction/langue`, `…/traduction/messages`, `POST …/traduction`.
 *
 * Les entrants portent déjà leur traduction (`ApiMessage.traductions`, posée
 * par la fabrique comme Cyrano la persiste) : le fil n'a rien à demander. Le
 * sens « envoyer » ne connaît que les phrases de `langues.ts` — celles que
 * Francis propose. Une phrase libre rend une ERREUR qui le dit, jamais le
 * français déguisé en traduction : un aperçu qui ment est pire que pas
 * d'aperçu.
 */
function laTraduction(conv: ConversationDemo, reste: string[], requete: RequeteDemo): unknown {
  if (reste[0] === "langue") return { langue: conv.langue, source: "texte" };
  if (reste[0] === "messages") {
    const corps = (requete.json ?? {}) as { ids?: string[] };
    const demandes = new Set(corps.ids ?? []);
    const traductions: Record<string, string> = {};
    for (const m of conv.messages) {
      if (demandes.has(m.id) && m.traductions?.fr) traductions[m.id] = m.traductions.fr;
    }
    return { langue: conv.langue, traductions, echecs: {} };
  }
  if (requete.methode !== "POST") return vide(requete.methode);

  const corps = (requete.json ?? {}) as { texte?: string; sens?: string };
  const texte = corps.texte ?? "";
  if (corps.sens === "comprendre") {
    const connu = conv.messages.find((m) => m.content === texte)?.traductions?.fr;
    return {
      texte: connu ?? texte,
      source: texte,
      depuis: conv.langue,
      vers: "fr",
      modele: "démo",
      deja_dans_la_langue: connu === undefined,
      provenance: connu ? "llm" : "",
    };
  }
  if (conv.langue === "fr") {
    return {
      texte,
      source: texte,
      depuis: "fr",
      vers: "fr",
      modele: "",
      deja_dans_la_langue: true,
      provenance: "",
    };
  }
  const traduit = traduireVers(texte, conv.langue);
  if (traduit === null) return TRADUCTION_INCONNUE;
  return {
    texte: traduit,
    source: texte,
    depuis: "fr",
    vers: conv.langue,
    modele: "démo",
    deja_dans_la_langue: false,
    provenance: "llm",
  };
}

function laListe(requete: RequeteDemo): ApiConversationListResponse {
  const p = requete.params ?? {};
  const liste = (cle: string): string[] => {
    const brut = p[cle];
    if (brut === null || brut === undefined || brut === "") return [];
    return String(brut).split(",").filter(Boolean);
  };
  const vrai = (cle: string): boolean => p[cle] === true || p[cle] === "true";

  const ids = liste("ids");
  const inclus = liste("creator_ids");
  const exclus = liste("exclude_creator_ids");
  const langues = liste("languages").map((l) => l.toUpperCase());
  const groupes = liste("stage_groups");
  const tags = liste("tags").map((t) => t.toLowerCase());
  const q = String(p.q ?? "").trim().toLowerCase();
  const creeApres = p.created_after ? Date.parse(String(p.created_after)) : NaN;

  let convs = toutesLesConversations();

  // Hydratation d'UNE carte hors page : les filtres d'affichage ne s'y
  // appliquent pas (c'est le contrat de `?ids=`).
  if (ids.length > 0) {
    convs = convs.filter((c) => ids.includes(c.fiche.id));
    return { conversations: convs.map(copie), next_cursor: null, stats: null };
  }

  if (inclus.length > 0) convs = convs.filter((c) => inclus.includes(c.fiche.creator_id));
  if (exclus.length > 0) convs = convs.filter((c) => !exclus.includes(c.fiche.creator_id));
  if (langues.length > 0)
    convs = convs.filter((c) => langues.includes((c.fiche.language ?? "").toUpperCase()));
  if (groupes.length > 0)
    convs = convs.filter((c) =>
      groupes.some((g) => GROUPES[g]?.(c.fiche.stage) ?? false),
    );
  if (tags.length > 0)
    convs = convs.filter((c) =>
      (c.fiche.tags ?? []).some((t) => tags.includes(t.name.toLowerCase())),
    );
  if (vrai("workflow_actif")) convs = convs.filter((c) => Boolean(c.fiche.workflow_actif));
  if (vrai("cyrano_pause")) convs = convs.filter((c) => c.fiche.is_cyrano_paused === true);
  if (vrai("pinned"))
    convs = convs.filter((c) =>
      (c.fiche.tags ?? []).some((t) => t.is_system && t.name === "PIN"),
    );
  if (!Number.isNaN(creeApres))
    convs = convs.filter((c) => Date.parse(c.fiche.created_at) >= creeApres);
  if (q)
    convs = convs.filter(
      (c) =>
        c.fiche.fan_name.toLowerCase().includes(q) ||
        c.fiche.creator_display_name.toLowerCase().includes(q),
    );

  const monde = leMonde();
  const total = monde ? monde.ordre.length : convs.length;
  const messages = toutesLesConversations().reduce(
    (n, c) => n + c.fiche.message_count,
    0,
  );
  const revenus = toutesLesConversations().reduce(
    (n, c) => n + c.fiche.revenue_cents,
    0,
  );

  return {
    conversations: convs.map(copie),
    next_cursor: null,
    stats: { conversations: total, messages, revenue_cents: revenus },
  };
}

/**
 * La frise du fan — dérivée du fil de la démo, pas inventée.
 *
 * C'est ce qui la rend juste sans entretien : les compteurs sortent des
 * messages qu'on a réellement posés, donc ils suivent tout ce qui se passe
 * pendant une prise (un envoi, une réponse du fan, un tour de Cyrano).
 */
function laFrise(conv: ConversationDemo): FanTimeline {
  const messages = conv.messages;
  const debut = Date.parse(messages[0]?.created_at ?? conv.fiche.created_at);
  const jour = (iso: string) =>
    Math.max(0, Math.floor((Date.parse(iso) - debut) / 86_400_000));
  const entrants = messages.filter((m) => m.direction === "in").length;

  const histogramme = new Map<number, { inbound: number; outbound: number }>();
  for (const m of messages) {
    const d = jour(m.created_at);
    const ligne = histogramme.get(d) ?? { inbound: 0, outbound: 0 };
    if (m.direction === "in") ligne.inbound += 1;
    else ligne.outbound += 1;
    histogramme.set(d, ligne);
  }

  return {
    conversation_id: conv.fiche.id,
    user_id: conv.fiche.user_id,
    creator_id: conv.fiche.creator_id,
    display_name: conv.fiche.fan_name,
    origin: { kind: "unknown", label: null },
    anchor_at: new Date(debut).toISOString(),
    span_days: jour(messages[messages.length - 1]?.created_at ?? conv.fiche.created_at) + 1,
    joined_at: conv.fiche.created_at,
    first_dm_at: messages[0]?.created_at ?? null,
    delay_days: null,
    messages_in: entrants,
    messages_out: messages.length - entrants,
    engaged_seconds: 0,
    paid_cents: conv.fiche.revenue_cents,
    currency: conv.fiche.currency,
    catalog_total: 0,
    events: [],
    events_dropped: 0,
    histogram: [...histogramme.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([d, l]) => ({ day: d, inbound: l.inbound, outbound: l.outbound })),
    status_segments: [],
    offers: [],
  };
}

/** Le cache de requêtes doit tenir SA copie : le monde, lui, continue de
 *  bouger sous les pieds (un message qui arrive recale la fiche). Servir la
 *  référence vive ferait des mises à jour invisibles — l'objet aurait changé
 *  sans que React ne le sache. */
function copie(c: ConversationDemo): ApiConversationListItem {
  return { ...c.fiche, tags: [...(c.fiche.tags ?? [])] };
}

/** Republie un événement arbitraire sur le flux (réservé aux mises en scène). */
export { emettre };
